/*
 * vms-slope.js
 * ---------------------------------------------------------------------------
 * The geometry of a TV hung PLUMB from a pipe under a SLOPED ceiling (a roof
 * underside: cathedral, attic knee wall, a garage rafter line). The ceiling
 * closes in ABOVE the screen as it rises, so the top back corner of the screen
 * can foul it. This file works out the pipe that keeps the corner clear.
 *
 * PURE GEOMETRY ONLY. No DOM, no drawing, no drag, no customer copy. That is
 * deliberate and it matches assets/vms-elevation.js exactly: the numbers below
 * are the part that has to be right, so they are kept separately testable and
 * cannot be broken by a UI change. The interactive scene and every string the
 * shopper reads are layered on top of this and read it, never the other way
 * round. The copy lives in theme settings; do not invent any here.
 *
 * ONE REFERENCE, MEASURED UPWARDS
 * Everything here is inches measured relative to the MIDDLE OF THE VESA HOLE
 * PATTERN, positive UPWARDS. Call that y = 0. That is the one point the TV, the
 * head, the pipe and the plate all share, so it is the honest origin. It is the
 * OPPOSITE sign convention to vms-elevation.js, which measures depth DOWNWARDS
 * from the building structure; the two files answer two different questions and
 * do not share arithmetic, only vocabulary. Do not import one into the other.
 *
 *   y = 0                 middle of the VESA hole pattern (the mount reference)
 *   y = +holeMid          the TOP OF THE SCREEN, holeMid above the pattern
 *   y = headOffset + pipe + plateOffset   the ceiling attachment point P
 *
 * WHAT THE CUSTOMER MEASURES, AND WHAT WE ALREADY KNOW
 *   topToTopHole   top edge of the TV down to the TOP mounting hole (measured)
 *   vesaHeight     the VESA pattern height, hole to hole vertical (measured)
 *   screenHeight   the screen height
 *   headOffset     custom.length_offset on the HEAD. POSITIVE means the head
 *                  threads onto the END of the pipe, so the pipe END sits
 *                  headOffset ABOVE y = 0. NEGATIVE means the pipe runs THROUGH
 *                  the head onto a collar (a sliding head); see headSlides().
 *   plateOffset    custom.length_offset on the PLATE. The ceiling attachment
 *                  point P sits plateOffset ABOVE the top of the pipe.
 *   riseOver12     roof pitch the standard builder's way: inches of rise per 12
 *                  of run. 12 over 12 is 45 degrees.
 *
 * [RESTING 2026-08-29] THE SLIDING HEAD RESTS ON THE THREADED COLLAR BY DEFAULT; THE 11FC IS OPTIONAL.
 * This SUPERSEDES the whole [CAP-RESTORED] window model below for the sliding head (owner ruling, evening
 * 2026-08-29). The head body bottom sits directly on the threaded end collar (no 11FC), so the placed head
 * drop is dRest(L) = plateOffset + L - HOLE_TO_PIPE_END_RESTING (6.161), the LOWEST screen position on that
 * pipe. There is NO cap and NO [floor, cap] window any more. A build is feasible iff (1) the resting drop
 * clears the corner, dRest(L) >= cornerFloor(tilt), a MINIMUM pipe with no maximum exactly like the fixed
 * head, and (2) the TV is tall enough that the collar bottom stays at or above the screen bottom AT REST,
 * which reduces to screenHeight - holeMid >= HOLE_TO_COLLAR_BOTTOM_RESTING (6.811) - a pure TV-geometry gate,
 * INDEPENDENT of pipe length (both the collar bottom and the screen bottom move down together with L), so no
 * pipe can fix it: that failure is its own reason 'collar_shows'. solve() recommends the SHORTEST stock pipe
 * at or above the resting floor and offers EVERY longer stock pipe. The 11FC is now ONLY an optional
 * headroom upgrade: adding it lets the head slide UP by rise11fc = (dRest(L) - COLLAR_11FC) - max(cornerFloor,
 * dMin) inches (the REALIZABLE rise; the raw gap dRest - max(cornerFloor, dMin) ignores the 11FC's 0.875 in height)
 * (screen higher, toward the corner limit); needs11fc is FALSE for every single-pipe build. The old MODE 2
 * (an 11FC-REQUIRED single pipe) was DROPPED: it is geometrically dominated (resting clears the corner with
 * a pipe 0.875 in shorter than any slide-up position and the bottom rule is easiest at rest), verified over
 * a 441,000-input sweep with zero counter-examples. The COUPLED STACK is DEFERRED (not built): a single
 * stock pipe up to the longest in the pool covers any window a stack could, because the resting bottom rule
 * is L-independent. HOLE_TO_PIPE_END (7.036) and dMin/dMax survive ONLY for the 11FC headroom arithmetic and
 * the drawing; they no longer gate feasibility. plateGap and pipeBelowScreen stay OUTPUTS (never gates).
 *
 * [SUPERSEDED 2026-08-29 by RESTING, above] THE SLIDING HEAD'S BOTTOM CAP IS A HARD RULE AGAIN, AND IT NOW INCLUDES THE
 * THREADED END COLLAR. This REVERSES the morning's "cap is an output" ruling for the BOTTOM only. Owner,
 * verbatim: "having the pipe show below the screen is an undesirable condition that we want to avoid." So
 * the sliding head has TWO hard rules again: the corner-clearance FLOOR (top, shared with the fixed head)
 * and the bottom CAP. The cap is the pipe that lands the THREADED COLLAR BOTTOM flush with the bottom edge
 * of the screen at the placed (highest) head position; the collar hangs THREADED_COLLAR_BELOW_PIPE_IN
 * (0.65 in, owner CAD) below the pipe end, so the cap includes it:
 *       cap = (placedD + screenHeight - holeMid) - plateOffset - THREADED_COLLAR_BELOW_PIPE_IN
 *             placedD = max(cornerFloor, dMin)   (the placed head drop; tilt-aware through cornerFloor)
 * solve() recommends the SHORTEST stock pipe in [floor, cap] and offers the longer IN-WINDOW stock pipes,
 * exactly the fixed-head shape but with an upper bound again. The ONLY sliding failure is 'no_pipe_fits'
 * (no stock pipe lies in [floor, cap]: the window is empty on a too-short screen, or falls between stock
 * sizes); floor and cap are reported so the page can name the range. The PLATE-ABOVE-the-screen rule stays
 * an OUTPUT: plateGap (signed, hidden vs proud) drives answer_mount_covered / answer_mount_visible and never
 * gates. pipeBelowScreen is redefined as the COLLAR bottom below the screen bottom (was the pipe end); it is
 * <= 0 for every feasible pipe by construction, kept only so the page can state it. slidingCap == cap (the
 * pipe at which pipeBelowScreen crosses 0). marginBottom stays INERT (the owner-CAD collar overhang is the
 * whole cap; marginBottom is not a term); it is still accepted for compatibility. See the sliding branch of
 * solve() and DEFAULT_MARGIN_BOTTOM below.
 *
 * THE MODEL (owner-settled 2026-08-24). Hanging the TV high enough to HIDE the
 * ceiling plate behind the screen is a LOOK some shoppers want, NOT a rule. So
 * there is exactly ONE hard constraint on a FIXED head, and it is a MINIMUM pipe
 * length with no maximum:
 *
 *   THE CORNER MUST CLEAR THE CEILING. Above P the ceiling rises toward the back
 *   of the screen, so the screen top may sit at most riseAvailable = standoff *
 *   rise / 12 above P. That is the minimum pipe:
 *       minPipe = holeMid - headOffset - plateOffset - riseAvailable
 *   There is NO maximum: the head threads onto the pipe END, so nothing protrudes
 *   below the TV and a longer pipe simply hangs the screen lower, which is a
 *   customer preference. solve() recommends the SHORTEST stock pipe at or above
 *   minPipe (top of the screen as close to the ceiling as the stock allows) and
 *   offers the next few longer stock pipes as options.
 *
 *   plateGap = (holeMid - headOffset - plateOffset) - pipe is an OUTPUT, reported
 *   as a plain fact: POSITIVE means the plate sits that far BELOW the screen top
 *   (hidden), NEGATIVE means it stands that far PROUD (visible). Neither is a
 *   failure. reason is 'ok' whenever any stock pipe clears the corner.
 *
 * [RULE] THERE IS EXACTLY ONE MARGIN IN THE WHOLE SYSTEM, AND IT IS AT THE BOTTOM.
 * DEFAULT_MARGIN_BOTTOM (0 in; the owner REMOVED the 3.0 in rule of thumb, see below and
 * do not restore it) is the least distance the BOTTOM END OF
 * THE PIPE must stay ABOVE THE BOTTOM EDGE OF THE TV, and it applies ONLY to the
 * sliding head (33CE008HEAD with the 11FC), where the pipe passes through the head
 * and can protrude below the screen. On the fixed heads (33HE001, 33HE002) the head
 * threads onto the pipe END, nothing can protrude below the TV, and NO margin of any
 * kind applies. There is NO top margin. An earlier draft carried a marginTop ("how
 * far below the screen top the plate must sit"); the owner ruled it does not exist,
 * and it has been deleted from this file, not deprecated.
 *
 * THE SLIDING HEAD (33CE008HEAD) IS DIFFERENT. The pipe runs THROUGH the head onto a
 * collar, so the head's POSITION on the pipe, not the pipe length, sets the screen
 * HEIGHT. The pipe still has a range [floor, cap]. Let d' be the drop of the hole centre
 * below the ceiling point P (the positioning variable):
 *       d'min   = holeMid - riseAvailable       (corner clears, screen as high as it goes)
 *       pipeMax = d' - holeMid + screenHeight - plateOffset - marginBottom
 *                 and at d'min that reduces to
 *                 screenHeight - plateOffset - marginBottom - riseAvailable   (pipeMaxSliding)
 * That pipeMax is the CAP: a longer pipe would protrude below the TV.
 *
 * THE FLOOR IS THE GREATER OF TWO REQUIREMENTS (bug fix 2026-08-24; reach identity corrected
 * 2026-08-28). The pipe must:
 *   REACH  from the ceiling plate down to where the head sits. [TRAVEL] With the head placed at
 *          d = max(cornerFloor, dMin) the pipe end is HOLE_TO_PIPE_END (7.036, the PHYSICAL figure)
 *          below the hole centre, and the pipe top is at plateOffset, so the reach floor is
 *          cornerFloor + HOLE_TO_PIPE_END - plateOffset. This REPLACES the old length_offset (-6.0)
 *          reach (d'min + 6.0 - plateOffset), which was the seed of the 11FC-over-collar drawing and
 *          came out 1.107 in short, so the head could slide low enough that the 11FC collar bottom
 *          overlapped the threaded end collar. The engine no longer reads length_offset for the sliding
 *          head; the metafield is unchanged. In almost every real build the reach floor binds.
 *   GRIP   at least minPipeForSlidingHead() = SLIDING_HEAD_SPAN + 2 * PIPE_ENGAGEMENT = 9.361
 *          in, or the head and collar cannot hold the pipe (owner-measured 2026-08-24). This is the
 *          ABSOLUTE least pipe the head can ever hold; it is exactly the dMin-binding case of the reach
 *          floor (dMin + HOLE_TO_PIPE_END - plateOffset = PIPE_ENGAGEMENT + HOLE_TO_SOCKET +
 *          HOLE_TO_PIPE_END = 9.361), and it binds only on very shallow builds where cornerFloor falls
 *          under dMin.
 * floor = max(reach, grip), and solve() recommends the SHORTEST stock pipe in [floor, cap]
 * exactly as the fixed head does, with the longer stock pipes in the range offered as options
 * and floorReason ('reach' or 'grip') naming which requirement set the floor. If the floor is
 * above the cap the screen is too short for the sliding head at all: reason 'screen_too_short',
 * no recommendation. [WARN] An earlier revision used the grip floor ALONE, which recommended a
 * pipe far too short to reach the head on a normal build; do not reinstate a grip-only floor.
 *
 * [TRAVEL] solve() also returns the head-drop band {min, max} and the travel window {dMin, dMax, travel}.
 * headDrop.min is the placed drop d = max(cornerFloor, dMin), the highest-screen install position;
 * headDrop.max is dMax = plateOffset + L - HOLE_TO_PIPE_END, the head at the pipe END, for the
 * recommended pipe (NO longer open/null: the pipe end is a hard physical stop). dMin =
 * plateOffset + PIPE_ENGAGEMENT + HOLE_TO_SOCKET is the top of the window (socket mouth
 * PIPE_ENGAGEMENT below the pipe top); travel = dMax - dMin = L - 9.361.
 *
 * WHY d'min IS THE RIGHT WORST CASE, AND WHY NO CUSHION IS NEEDED. d'min is a physical
 * extreme, not a chosen target: slide the head any higher than d'min and the screen's
 * top back corner touches the ceiling, so the customer cannot go there. Sizing the
 * pipe against d'min therefore already covers every position the customer can actually
 * reach; adding a safety margin on top of a physical limit would be doubling up. That
 * is why marginBottom defaults to 0.
 *
 * [WARN] THE FIGURE 24.650 IS STALE. It appears in SPEC-slanted-wall-page-2026-08-23.html
 * as the sliding cap, and it came from screenHeight - plateOffset - 3 - 3, i.e. the
 * OLD two-margin model: three inches subtracted at BOTH ends. Both of those three-inch
 * subtractions are now gone, for different reasons. The TOP one because no such rule
 * ever existed (the owner ruled marginTop does not exist). The BOTTOM one because the
 * owner removed the rule-of-thumb cushion (marginBottom now defaults to 0). The
 * corrected sliding cap is screenHeight - plateOffset - riseAvailable - marginBottom,
 * which on the owner's 70 inch example at 9 in 12 is 27.901 at the default marginBottom
 * of 0 (and 24.901 if a 3 inch cushion is ever re-added). Do NOT reconcile this code to
 * the spec by putting either subtraction back; the spec is the superseded document.
 *
 * [NOTE] THE SLIDING HEAD'S MINIMUM PIPE IS NOW WRITTEN (owner-measured in CAD 2026-08-24).
 * The head and its collar grip SLIDING_HEAD_SPAN = 8.661 in of pipe, from where the pipe
 * enters the 33CE008HEAD down to the bottom of the 11FC collar; with PIPE_ENGAGEMENT = 0.35
 * given up at EACH end the shortest usable pipe is minPipeForSlidingHead() = 9.361 in, so 10
 * in is the shortest stock pipe that works. This SUPERSEDES the earlier 8.786 candidate,
 * which came from the CAD enclosure row (pipe enclosed 170.926 to 394.090 below P0, about
 * 223.164 mm) and was an inference, not a measurement. Do not reinstate 8.786.
 *
 * [RULE] pipeWindow() IS KEPT, EXPORTED AND TESTED, but it NO LONGER GATES anything.
 * It now describes the OPTIONAL "I would also like the plate hidden AND the corner
 * clear" preference: max is the pipe that lands the plate flush with the screen top
 * (plateGap 0), min is the pipe that clears the corner (plateGap = riseAvailable, the
 * most hidden). A caller may use it to highlight a hidden-plate option, but solve()'s
 * answer is driven by the corner minimum alone. Do NOT reinstate an upper bound as a
 * rule; that is the exact mistake the 2026-08-24 revision corrects.
 *
 * [WARN] THE STANDOFF. It is the horizontal pipe axis to back of screen distance.
 * The three live heads are now OWNER-MEASURED (2026-08-24; see MEASURED_STANDOFF),
 * but the UNKNOWN path is permanent: a head with no measurement, or a field the
 * owner has blanked, must still flow through as unknown, not as a fabricated value.
 * An unknown standoff keeps its honest shape: minPipe is null, feasible is null,
 * reason is 'standoff_unknown'. NEVER substitute a default standoff, NEVER treat
 * unknown as zero or as infinity. See num() and riseAvailable() for how the unknown
 * is guarded, and why isNaN alone would have let it through as a confident zero.
 *
 * WHAT A CUSTOMER CALLS AN ANGLED WALL (owner, 2026-08-23). The math here is valid at
 * any pitch, but the shopper's VOCABULARY is not: someone shopping for an angled WALL
 * mount is not picturing a 4 in 12 or 6 in 12 attic roof, which they would call an
 * angled CEILING. Roughly 9 in 12 and up is where "wall" language starts. WALL_PITCH_MIN
 * captures that and readsAsWall() tests it. It is a TERMINOLOGY and search-intent
 * threshold ONLY, not a geometry limit: nothing in this file refuses, clamps, or
 * changes a number below it, and solve() returns fully correct figures at 4 in 12.
 * The UI alone decides what to SAY.
 */
(function () {
  'use strict';

  // [CAP-RESTORED 2026-08-29] THIS MARGIN IS INERT. The sliding CAP is back as a HARD rule, but it is the
  // THREADED COLLAR cap (owner-CAD collar overhang, THREADED_COLLAR_BELOW_PIPE_IN), NOT a marginBottom
  // cushion: solve() computes the cap from the collar overhang and never reads marginBottom into any output.
  // The constant and the margin_bottom setting are kept ONLY so the two standalone helpers pipeMaxSliding
  // and minScreenHeightForSlidingHead (both vestigial, neither called by solve) still accept it. Do not
  // wire a new consumer, and do not fold marginBottom back into the cap.
  //
  // THE ONLY MARGIN IN THE SYSTEM (owner-set 2026-08-24), AND ITS DEFAULT IS ZERO.
  // It is the least distance the BOTTOM END OF THE PIPE must stay ABOVE THE BOTTOM
  // EDGE OF THE TV, and it applies to the SLIDING head only (33CE008HEAD with the
  // 11FC), where the pipe passes through the head and can protrude below the screen.
  // The owner EXPLICITLY REMOVED the arbitrary 3 inch rule-of-thumb cushion: the real
  // constraint is only that the pipe not show below the TV at the highest position the
  // customer can slide the head to, and d'min is already that physical extreme (see
  // the header), so no cushion is needed on top of a physical limit. The constant and
  // the margin_bottom theme setting are KEPT at 0 so a cushion can be re-added later
  // without a code push. It is NOT a general safety factor and there is NO top margin.
  var DEFAULT_MARGIN_BOTTOM = 0.0;

  // A head length_offset AT OR BELOW this slides on the pipe instead of threading
  // onto its end. The live sliding head (33CE008HEAD) is -6.0, the fixed heads are
  // positive, so the boundary is 0 and a zero offset counts as sliding.
  var SLIDING_HEAD_OFFSET_MAX = 0;

  // AUDIENCE / TERMINOLOGY threshold, NOT a geometry limit (owner, 2026-08-23).
  // At or above this rise over 12 a shopper calls the surface an angled WALL;
  // below it they call it an angled CEILING. It steers search intent and what the
  // UI says, nothing else. The geometry in this file is valid at any pitch and no
  // function refuses or clamps below this value. See the header.
  var WALL_PITCH_MIN = 9;

  // Owner-measured head standoffs, supplied 2026-08-24: the horizontal distance
  // from the TV mounting surface to the CENTRE AXIS of the pipe, in inches. The
  // 33HE001 and 33HE002 fixed heads read 2.716; the 33CE008HEAD sliding head reads
  // 3.665, OWNER-CONFIRMED as the ZERO TILT figure (2026-08-24): it is the standoff
  // with the head squared up, not tilted, so the earlier worry that 3.665 might be a
  // 10 degree shipped-pose reading is CLOSED. Exported for the tests and for
  // documentation ONLY. The SECTION SETTING (#vmsw-standoff in
  // sections/vms-slanted-wall.liquid) stays the SOURCE OF TRUTH at runtime, so the
  // owner can override any of these in the theme editor without a code push; the page
  // does not read this constant. Do not wire the page to it.
  // 22HE001 added 2026-08-25 from owner CAD: 59.747 mm = 2.352 in from the pipe centre
  // axis to the VESA mounting surface. This SUPERSEDES the earlier 2.347 in that was
  // read off the side render at the 12 degree pose (owner-to-confirm); the CAD figure
  // governs. Documentation-only like the rest of this table; the page still reads the
  // #vmsw-standoff section setting at runtime.
  var MEASURED_STANDOFF = { '33HE001': 2.716, '33HE002': 2.716, '33CE008HEAD': 3.665, '22HE001': 2.352 };

  // THE SLIDING HEAD BODY AND ITS COLLAR (owner-measured 2026-08-28), the two pieces the pipe
  // passes through, in inches. HEAD_BODY is the part of the 33CE008HEAD the pipe runs through,
  // from the socket mouth at the top down to the body bottom; COLLAR_11FC is the 11FC that mates
  // to the head body bottom and hangs below it. Their sum is the grip span (below).
  var HEAD_BODY = 7.786;      // socket mouth down to the head body bottom (the pipe runs through this)
  var COLLAR_11FC = 0.875;    // the 11FC collar, mates to the head body bottom

  // THE SLIDING HEAD'S GRIP SPAN (owner-measured in CAD 2026-08-24; now DERIVED 2026-08-28). The
  // pipe must run continuously from where it enters the 33CE008HEAD down to the bottom of the 11FC
  // threaded collar, and the head can only hold the pipe over that span. Owner, verbatim: "I measured
  // in the CAD model and it is 8.661 inches where the pipe enters the 33CE008HEAD down to the bottom
  // of the 11FC." That figure is EXACTLY HEAD_BODY + COLLAR_11FC (7.786 + 0.875 = 8.661), so it is now
  // DERIVED from those two owner measurements rather than retyped; the test asserts the identity.
  // SUPERSEDES the earlier 8.786 candidate, which was read off the CAD enclosure row (about 223.164 mm)
  // and was my inference, not a measurement; do not reinstate 8.786.
  var SLIDING_HEAD_SPAN = HEAD_BODY + COLLAR_11FC;   // 8.661, pipe entry at the head down to the bottom of the 11FC.

  // Pipe consumed at EACH end when it is engaged: this much runs up into the ceiling plate
  // at the top and into the threaded collar at the bottom, so it is not free span. Same
  // house constant already used by scripts/ortho-cutouts/placement.py and
  // assets/vms-assembly-art.js ("The ceiling plates overlay the top of the pipe .35 inches").
  var PIPE_ENGAGEMENT = 0.35;

  // [TRAVEL] THE SLIDING HEAD'S PHYSICAL TRAVEL WINDOW ON THE PIPE (owner-measured data 2026-08-28).
  // The 33CE008HEAD slides along the pipe, so its POSITION, not the pipe length, sets the screen
  // height. Two owner figures below fix the window the head can occupy on a pipe of length L, both
  // measured from the VESA hole centre (y = 0), and they REPLACE the head length_offset (-6.0) in the
  // sliding reach identity: -6.0 was a drawing seed (the 11FC-over-collar stack) and came out 1.107 in
  // short of the real reach. See the [TRAVEL] block in the sliding branch of solve().
  //
  //   HOLE_TO_SOCKET     the socket mouth (where the pipe enters the head) sits this far ABOVE the hole
  //                      centre. OWNER CAD 2026-08-29, measured off the dimensioned drawing and it
  //                      SUPERSEDES the earlier derived 1.904: the head body top (the socket mouth) is
  //                      6.689 in below the bracket top, the bracket is 17.327 in tall, and the VESA hole
  //                      centre is centred at 8.664 in (17.327 / 2), so the socket mouth is 8.664 - 6.689 =
  //                      1.975 in above the hole centre. No longer a "pending" derivation; this is the
  //                      owner-measured figure.
  //   HOLE_TO_PIPE_END   how far the pipe END sits BELOW the hole centre when the head is at the very end
  //                      of the pipe (its lowest install). The pipe end is PIPE_ENGAGEMENT below the 11FC
  //                      bottom, the 11FC bottom is COLLAR_11FC below the head body bottom, and the body
  //                      bottom is HEAD_BODY - HOLE_TO_SOCKET below the hole centre:
  //                      HOLE_TO_PIPE_END = HEAD_BODY - HOLE_TO_SOCKET + COLLAR_11FC + PIPE_ENGAGEMENT = 7.036.
  //
  // [NOTE] HOLE_TO_SOCKET is the ONLY place this datum lives; HOLE_TO_PIPE_END is derived from it, so a
  // single edit to the socket figure updates the whole travel window.
  var HOLE_TO_SOCKET = 1.975;   // socket mouth above the hole centre; owner CAD 2026-08-29 (was derived 1.904)
  var HOLE_TO_PIPE_END = HEAD_BODY - HOLE_TO_SOCKET + COLLAR_11FC + PIPE_ENGAGEMENT;   // 7.036

  // [RESTING 2026-08-29] HOW FAR THE PIPE END SITS BELOW THE HOLE CENTRE WHEN THE HEAD RESTS ON THE THREADED
  // COLLAR (no 11FC). The head body bottom is HEAD_BODY - HOLE_TO_SOCKET (5.811) below the hole centre and
  // sits on the collar top, which is PIPE_ENGAGEMENT (0.35) above the pipe end, so the pipe end is
  // 5.811 + 0.35 = 6.161 below the hole centre. This is HOLE_TO_PIPE_END (7.036) MINUS the 11FC height
  // COLLAR_11FC (0.875): resting drops the head the full 11FC lower than the 11FC-in "head at the pipe end"
  // position. It is the placed drop for a resting build, dRest(L) = plateOffset + L - HOLE_TO_PIPE_END_RESTING.
  var HOLE_TO_PIPE_END_RESTING = HEAD_BODY - HOLE_TO_SOCKET + PIPE_ENGAGEMENT;   // 6.161 (= HOLE_TO_PIPE_END - COLLAR_11FC)

  // [CAP-RESTORED 2026-08-29] THE THREADED END COLLAR HANGS BELOW THE PIPE END. The threaded collar at the
  // bottom of the pipe is 1.0 in tall and seats PIPE_ENGAGEMENT (0.35 in) OVER the pipe end, so its bottom
  // face sits THREADED_COLLAR_BELOW_PIPE_IN = 1.0 - 0.35 = 0.65 in BELOW the pipe end (owner CAD 2026-08-29).
  // The owner reversed the morning's "cap is an output" ruling for the BOTTOM: a pipe (with its collar)
  // showing below the bottom edge of the screen is an undesirable condition to avoid, so the sliding cap is
  // a HARD rule again and it is the pipe that lands this COLLAR BOTTOM flush with the screen bottom. See the
  // sliding branch of solve() and the [CAP-RESTORED] block in the header.
  var THREADED_COLLAR_BELOW_PIPE_IN = 1.0 - 0.35;   // 0.65 in, threaded collar bottom below the pipe end

  // [RESTING 2026-08-29] HOW FAR THE THREADED COLLAR BOTTOM SITS BELOW THE HOLE CENTRE AT REST: the pipe end
  // is HOLE_TO_PIPE_END_RESTING (6.161) below the hole centre and the collar hangs THREADED_COLLAR_BELOW_PIPE_IN
  // (0.65) below the pipe end, so the collar bottom is 6.811 below the hole centre. Because BOTH the collar
  // bottom and the screen bottom move down together with the pipe length, whether the collar shows below the
  // screen at rest depends ONLY on the TV: the collar is hidden iff screenHeight - holeMid >= this figure.
  // A TV too short (screenHeight - holeMid < 6.811) yields reason 'collar_shows' and no pipe can fix it.
  var HOLE_TO_COLLAR_BOTTOM_RESTING = HOLE_TO_PIPE_END_RESTING + THREADED_COLLAR_BELOW_PIPE_IN;   // 6.811

  // Standard builder's run. Pitch is always expressed over 12 in of run.
  var RUN = 12;

  // Float dust tolerance for boundary comparisons. Six orders of magnitude below
  // anything a pipe can resolve, so it can never swallow a real shortfall; it only
  // stops a tie (a stock pipe sitting exactly at minPipe, or riseAvailable exactly
  // at a floor) from being mis-read by binary rounding. Same discipline as TIE_EPS
  // in vms-elevation.js.
  var EPS = 1e-9;

  /*
   * num(v) -> a finite number, or null.
   *
   * [WARN] THIS GUARD IS THE WHOLE SAFETY OF THE UNKNOWN-STANDOFF RULE, so it is
   * written to reject the two values a naive check waves through. Number(null) is
   * 0 and Number('') is 0, NOT NaN, so a test written only with isNaN would
   * silently accept a MISSING standoff as a standoff of zero and produce a
   * confidently wrong answer (a real pitch that clears, reported as fouling, or the
   * reverse). There is a documented precedent for exactly this bug in this
   * codebase: ratingOf / cheapestPlateOf both took Number() of an absent field
   * and treated the resulting 0 as data. So null, undefined and '' (including
   * all-whitespace) are rejected BEFORE Number() ever runs, and every numeric
   * input in this file is routed through here. A numeric string like '4' is
   * accepted, an unparseable one like 'abc' becomes NaN and is rejected.
   */
  function num(v) {
    if (v === null || v === undefined) return null;
    if (typeof v === 'string' && v.trim() === '') return null;
    var n = Number(v);
    return typeof n === 'number' && isFinite(n) ? n : null;
  }

  /** Pitch as an angle from horizontal, in DEGREES. atan2(rise, 12). Null if unusable. */
  function pitchDegrees(riseOver12) {
    var r = num(riseOver12);
    return r === null ? null : Math.atan2(r, RUN) * 180 / Math.PI;
  }

  /** The inverse: a pitch angle in degrees back to rise over 12. Round-trips pitchDegrees. */
  function pitchFromDegrees(deg) {
    var d = num(deg);
    return d === null ? null : RUN * Math.tan(d * Math.PI / 180);
  }

  /**
   * The top of the screen above the mount reference, from the two measured
   * numbers. holeMid = topToTopHole + vesaHeight / 2: down from the top edge to
   * the top hole, then half the pattern to its middle, which is y = 0. Null if
   * either input is unusable.
   */
  function holeMidFromTop(topToTopHole, vesaHeight) {
    var t = num(topToTopHole), v = num(vesaHeight);
    return (t === null || v === null) ? null : t + v / 2;
  }

  /**
   * How far the screen top may sit above the ceiling point P, in inches: the
   * horizontal run to the back of the screen times the pitch.
   *
   * [WARN] NULL when the standoff is unknown, and that is the honest answer, not a
   * zero. standoff null, '' or non-finite gives null here, which is what makes the
   * corner minimum and the sliding cap carry the unknown through rather than
   * fabricating a number. A standoff of exactly 0 is a DIFFERENT thing: it is a
   * known, real, flush mount with no run, so it returns 0. Do not conflate the two.
   */
  function riseAvailable(standoff, riseOver12) {
    var s = num(standoff), r = num(riseOver12);
    return (s === null || r === null) ? null : s * r / RUN;
  }

  /** True when the head SLIDES on the pipe (offset at or below the boundary), i.e. negative or zero. */
  function headSlides(headOffset) {
    var h = num(headOffset);
    return h !== null && h <= SLIDING_HEAD_OFFSET_MAX;
  }

  /**
   * Whether a pitch reads to a shopper as an angled WALL rather than an angled
   * CEILING. TERMINOLOGY ONLY (owner, 2026-08-23): it changes what the UI says and
   * nothing about the geometry. Null when the pitch is unknown, per the num() rule,
   * so a caller can tell "not a wall" apart from "we do not know the pitch".
   */
  function readsAsWall(riseOver12) {
    var r = num(riseOver12);
    return r === null ? null : r >= WALL_PITCH_MIN;
  }

  /*
   * [TILT] SCREEN TILT (owner 2026-08-25). The shopper may angle the TV DOWN, up to the
   * head's own maximum downward tilt (the data snippet's tilt_down per head). Tilting DOWN
   * swings the TOP of the screen FORWARD (+x, toward the room) and the bottom back, and the
   * TOP BACK CORNER, the one and only corner the clearance rule checks, DROPS. A dropped
   * corner clears the closing ceiling with LESS pipe, so the mount can be raised and the
   * screen sits higher, which is exactly the point of the feature. The BOTTOM corner is
   * never a concern (owner ruling), so only the top back corner is rotated and re-checked.
   *
   * THE TILT AXIS sits `drop` inches BELOW the hole-centre height (y = -drop; tilt_axis_drop,
   * positive = below, owner CAD 2026-08-25) and `pivot` inches BEHIND the VESA mounting surface
   * (toward the pipe), i.e. at (x = standoff - pivot, y = -drop). `pivot` and `drop` are per-head
   * owner CAD; a BLANK pivot means the axis is assumed to lie AT the mounting surface (pivot = 0),
   * a blank drop means AT the hole-centre height (drop = 0). Both are EXPLICIT branches
   * (tiltPivotOrZero / num()-then-0), never Number('') coercing to 0, the same discipline as num():
   * blank is a deliberate assumption we name, not a value that leaked through.
   *
   * tiltPoint() rotates one TV point about that axis by tiltDeg DOWN. tiltDeg 0 (the default
   * everywhere) is the IDENTITY and returns the point unchanged, so every zero-tilt result and
   * the whole existing suite are byte-for-byte untouched. Standoff is needed for the axis x, so
   * a null standoff yields {x:null, y:...} and the unknown carries through riseAvailable exactly
   * as before.
   *
   * [TILT-MIGRATE] THE 33CE008HEAD IS A MIGRATING PIVOT, NOT A FIXED AXIS. Its CAD joint is a
   * slotted arc whose instantaneous rotation centre travels as it tilts, so it is NOT approximated
   * by a single pivot/drop. Instead the caller passes `tiltTable` (MIGRATING_VESA_PATH[sku]): a
   * per-degree table of the VESA-centre DISPLACEMENT from t00 (dx "toward the wall", negative in the
   * CAD IMAGE frame; dyUp up), interpolated linearly between whole degrees (migrateDisplacement).
   * tiltPoint then PLACES the hole centre at (standoff - dx, dyUp) - the MINUS is the image-to-page
   * MIRROR (the page draws +x toward the ROOM and mirrors the head image, so the delivery's image-left
   * "toward the wall" is page +x toward the room; see the [MIRROR] note at the call) - and rotates the
   * TV point about that DISPLACED hole centre by t. The travel is what lets the screen sit higher. See
   * section 7 of the 2026-08-25 head-tilt-stills delivery.
   */
  // [TILT-MIGRATE] The 33CE008HEAD VESA-centre displacement path, from section 7 of the
  // 2026-08-25 head-tilt-stills delivery (dVESA_x_in toward the wall = negative dx; dVESA_y_up_in
  // = dyUp). Generated from the delivery's section-8 CSV, not retyped. Index = whole degree 0..10.
  // tiltPoint reads it via opts.tiltTable; the section/controller passes MIGRATING_VESA_PATH[sku].
  var MIGRATING_VESA_PATH = {
    '33CE008HEAD': [
      { dx: 0, dyUp: 0 },
      { dx: -0.0494, dyUp: 0.0186 },
      { dx: -0.0991, dyUp: 0.0363 },
      { dx: -0.149, dyUp: 0.0532 },
      { dx: -0.1993, dyUp: 0.0692 },
      { dx: -0.2499, dyUp: 0.0843 },
      { dx: -0.3025, dyUp: 0.0948 },
      { dx: -0.3554, dyUp: 0.1044 },
      { dx: -0.4084, dyUp: 0.113 },
      { dx: -0.4615, dyUp: 0.1208 },
      { dx: -0.5148, dyUp: 0.1276 }
    ]
  };

  function tiltPivotOrZero(pivot) {
    // BLANK/absent pivot -> axis AT the mounting surface (pivot = 0), an explicit named
    // assumption (owner CAD pending 2026-08-25), NOT a coerced Number(''). A real numeric
    // pivot, including a real 0, is honoured as given.
    var p = num(pivot);
    return p === null ? 0 : p;
  }
  // [TILT-MIGRATE] Interpolate the VESA-centre displacement (inches) for a migrating head at a
  // fractional degree t. table[i] = { dx, dyUp } is the CAD displacement from t00 at whole degree
  // i (dx negative = toward the wall, dyUp positive = up). Clamps below 0 and above the last whole
  // degree; linear between the two bracketing whole degrees. Null for an empty/absent table.
  function migrateDisplacement(table, t) {
    if (!table || !table.length) return null;
    var last = table.length - 1;
    var tt = num(t); if (tt === null) tt = 0;
    if (tt <= 0) return { dx: table[0].dx, dyUp: table[0].dyUp };
    if (tt >= last) return { dx: table[last].dx, dyUp: table[last].dyUp };
    var lo = Math.floor(tt), f = tt - lo, hi = lo + 1;
    return {
      dx: table[lo].dx + (table[hi].dx - table[lo].dx) * f,
      dyUp: table[lo].dyUp + (table[hi].dyUp - table[lo].dyUp) * f
    };
  }
  function tiltPoint(opts) {
    opts = opts || {};
    var x = num(opts.x), y = num(opts.y), s = num(opts.standoff);
    var t = num(opts.tiltDeg); if (t === null) t = 0;
    // Identity at zero tilt: return the point EXACTLY, no trig, so cornerX == standoff and
    // cornerY == holeMid to the bit and no float dust enters a zero-tilt comparison.
    if (t === 0) return { x: x, y: y };
    if (x === null || y === null || s === null) return { x: null, y: null };
    var rad = t * Math.PI / 180, ct = Math.cos(rad), st = Math.sin(rad);
    // [TILT-MIGRATE] 33CE008HEAD: the hole centre DISPLACES per the CAD path table and the TV
    // rotates about that displaced hole centre. See the header [TILT-MIGRATE].
    //
    // [MIRROR] The delivery table is in the CAD IMAGE frame, where dx negative = "toward the wall
    // (image left)". The page/engine frame has +x = toward the ROOM, and the head image is drawn
    // MIRRORED (room on the right), so image-left maps to page +x: the page displacement is the
    // NEGATION of the table dx. This is the same mirror the fixed heads obey (their VESA also swings
    // toward the room as they tilt down, matching their frames to ~1 px); feeding the raw image dx
    // detaches the simulated TV from the head-image VESA plate by ~1 in at full tilt (measured on dev
    // 2026-08-25). dyUp is vertical and the mirror leaves it unchanged. The stored table stays faithful
    // to the delivery numbers; the flip lives here, named.
    if (opts.tiltTable) {
      var d = migrateDisplacement(opts.tiltTable, t);
      if (d === null) return { x: null, y: null };
      var pageDx = -d.dx;                        // image "toward the wall" -> page "toward the room"
      var ox = x - s, oy = y;                    // offset from the ORIGINAL hole centre (s, 0)
      return { x: (s + pageDx) + ox * ct + oy * st, y: d.dyUp - ox * st + oy * ct };
    }
    var p = tiltPivotOrZero(opts.tiltPivot);
    var drop = num(opts.tiltAxisDrop); if (drop === null) drop = 0;   // + = axis BELOW the hole centre
    var axisX = s - p, axisY = -drop;
    var dx = x - axisX, dy = y - axisY;
    // Clockwise by t in this y-up frame: a point ABOVE the axis (dy > 0) swings toward +x
    // (top forward), which is what "tilt down" means. See the header [TILT].
    return { x: axisX + dx * ct + dy * st, y: axisY - dx * st + dy * ct };
  }

  /**
   * THE HARD MINIMUM PIPE for a FIXED head: the corner-clearance floor.
   *   minPipe = holeMid - headOffset - plateOffset - riseAvailable
   * There is NO maximum (see the header). Null when the standoff is unknown
   * (riseAvailable null) or the geometry is missing, so the caller suppresses the
   * pick rather than guessing. May be negative, which simply means even the
   * shortest stock pipe clears the corner.
   *
   * [TILT] With tiltDeg > 0 the corner is the TOP BACK corner (standoff, holeMid) ROTATED
   * DOWN about the tilt axis (tiltPoint), so both its height and its horizontal offset move:
   * minPipe = cornerY - headOffset - plateOffset - riseAvailable(cornerX). At tiltDeg 0 the
   * corner is (standoff, holeMid) exactly and this is the original formula, unchanged.
   */
  function minPipeForCorner(opts) {
    opts = opts || {};
    var holeMid = num(opts.holeMid);
    var headOffset = num(opts.headOffset);
    var plateOffset = num(opts.plateOffset);
    var corner = tiltPoint({ x: opts.standoff, y: holeMid, standoff: opts.standoff,
                             tiltDeg: opts.tiltDeg, tiltPivot: opts.tiltPivot,
                             tiltAxisDrop: opts.tiltAxisDrop, tiltTable: opts.tiltTable });
    var ra = riseAvailable(corner.x, opts.riseOver12);   // rise budget at the corner's x offset
    if (holeMid === null || headOffset === null || plateOffset === null ||
        corner.y === null || ra === null) return null;
    return corner.y - headOffset - plateOffset - ra;
  }

  /**
   * THE ABSOLUTE MINIMUM PIPE for the SLIDING head: the grip span plus the pipe given up at
   * each end. A CONSTANT, because it depends only on the head and the collar, not on the
   * TV, the plate or the pitch:
   *   minPipeForSlidingHead = SLIDING_HEAD_SPAN + 2 * PIPE_ENGAGEMENT = 9.361 in
   * A 9 in pipe leaves only 9 - 0.35 - 0.35 = 8.300 in to grip, short of the 8.661 needed, so
   * 10 in is the shortest stock pipe that can EVER hold this head.
   *
   * [WARN] THIS IS NOT THE OPERATIVE FLOOR FOR A GIVEN BUILD, and an earlier comment here that
   * implied it was is WRONG. 9.361 is the least pipe the head and collar can grip AT ALL, and it
   * binds only on very shallow builds. In almost every real build the pipe must also physically
   * REACH from the ceiling plate down to where the head sits, and that reach floor (minPipeForCorner,
   * the same fixed-head formula) is larger and is what actually binds. solve() takes the GREATER of
   * this grip floor and the reach floor; see the sliding branch of solve() and the header.
   */
  function minPipeForSlidingHead() {
    return SLIDING_HEAD_SPAN + 2 * PIPE_ENGAGEMENT;
  }

  /**
   * [RESTING 2026-08-29] THE ABSOLUTE MINIMUM PIPE for the SLIDING head WHEN IT RESTS ON THE THREADED COLLAR
   * (no 11FC), the operative grip floor now. Only the HEAD BODY runs through the head (the 11FC is not in the
   * stack), so the continuous pipe needed is PIPE_ENGAGEMENT at the plate + HEAD_BODY through the head body +
   * PIPE_ENGAGEMENT into the threaded collar = 0.35 + 7.786 + 0.35 = 8.486 in. A 9 in pipe qualifies (it did
   * NOT under the old 9.361 11FC-in grip). This is 0.875 shorter than minPipeForSlidingHead() precisely
   * because the 11FC (COLLAR_11FC) is out of the stack when the head rests. solve() takes the greater of this
   * grip floor and the resting reach floor (cornerFloor + HOLE_TO_PIPE_END_RESTING - plateOffset).
   */
  function minPipeForSlidingHeadResting() {
    return PIPE_ENGAGEMENT + HEAD_BODY + PIPE_ENGAGEMENT;
  }

  /**
   * Signed inches between the top of the screen and the top of the plate, for a
   * given pipe: (holeMid - headOffset - plateOffset) - pipe. POSITIVE means the
   * plate sits that far BELOW the screen top and is hidden; NEGATIVE means it stands
   * that far PROUD and is visible. An OUTPUT reported to the shopper as a plain fact,
   * never a pass or fail. Null when the geometry or the pipe is unusable.
   */
  function plateGap(opts) {
    opts = opts || {};
    var holeMid = num(opts.holeMid);
    var headOffset = num(opts.headOffset);
    var plateOffset = num(opts.plateOffset);
    var pipe = num(opts.pipe);
    if (holeMid === null || headOffset === null || plateOffset === null || pipe === null) return null;
    return holeMid - headOffset - plateOffset - pipe;
  }

  /**
   * THE OPTIONAL "PLATE HIDDEN AND CORNER CLEAR" WINDOW. Kept and exported, but it no
   * longer gates solve() (see the header [RULE]). There is no margin term any more.
   *   zeroMarginPipe = holeMid - headOffset - plateOffset   (plate flush with screen top)
   *   max = zeroMarginPipe                                  (plate flush, plateGap 0)
   *   min = zeroMarginPipe - riseAvailable                  (corner just clears, most hidden)
   * The window is always non-empty when the standoff is known and the geometry is
   * usable, because riseAvailable is never negative. reason:
   *   'standoff_unknown'  the standoff is not known, so min is null
   *   'window_empty'      the standoff IS known but the geometry is not (holeMid,
   *                       headOffset or plateOffset missing, so no flush pipe forms),
   *                       or, defensively, min exceeds max (cannot occur while
   *                       riseAvailable is non-negative). See :393 and :399.
   *   'screen_too_short'  zeroMarginPipe is negative: the plate cannot even reach
   *                       flush with the screen top, so it can never be hidden
   *   'ok'                a real hidden-plate window exists
   *
   * @returns {{zeroMarginPipe:(number|null), max:(number|null), min:(number|null),
   *            riseAvailable:(number|null), feasible:(boolean|null), reason:string}}
   */
  function pipeWindow(opts) {
    opts = opts || {};
    var holeMid = num(opts.holeMid);
    var headOffset = num(opts.headOffset);
    var plateOffset = num(opts.plateOffset);

    // The pipe that lands the plate exactly flush with the screen top.
    var zeroMarginPipe = (holeMid === null || headOffset === null || plateOffset === null)
      ? null
      : holeMid - headOffset - plateOffset;

    // The flush pipe is the longest that still hides the plate (plateGap 0 or more).
    var max = zeroMarginPipe;

    // Corner-clearance floor. Needs the standoff, so it carries the unknown through.
    var ra = riseAvailable(opts.standoff, opts.riseOver12);
    var min = (zeroMarginPipe === null || ra === null) ? null : zeroMarginPipe - ra;

    var feasible, reason;
    if (num(opts.standoff) === null) {
      feasible = null;
      reason = 'standoff_unknown';
    } else if (max === null || min === null) {
      // Standoff known but the geometry is not: we cannot form a window.
      feasible = false;
      reason = 'window_empty';
    } else if (max < 0) {
      feasible = false;
      reason = 'screen_too_short';
    } else if (min > max + EPS) {
      // Cannot occur while riseAvailable is non-negative; kept as a defensive guard.
      feasible = false;
      reason = 'window_empty';
    } else {
      feasible = true;
      reason = 'ok';
    }

    return {
      zeroMarginPipe: zeroMarginPipe,
      max: max,
      min: min,
      riseAvailable: ra,
      feasible: feasible,
      reason: reason
    };
  }

  /**
   * [CAP-RESTORED 2026-08-29] VESTIGIAL. solve() DOES gate on a cap again, but it computes the THREADED
   * COLLAR cap inline (placedD + screenHeight - holeMid - plateOffset - THREADED_COLLAR_BELOW_PIPE_IN) and
   * does NOT call this helper or read marginBottom. This standalone helper is the OLD marginBottom-aware,
   * collar-UNAWARE pipe-max figure; solve()'s slidingCap is exactly this MINUS the 0.65 collar overhang
   * (at the corner-binding drop). It is kept, exported and tested for callers that still want the old
   * figure, but it drives nothing in the page any more.
   *
   * THE SLIDING HEAD CAP: the longest pipe whose bottom end still stays marginBottom
   * above the bottom edge of the TV at the highest screen position (owner-derived
   * 2026-08-24):
   *   pipeMax = screenHeight - plateOffset - riseAvailable - marginBottom
   * It needs the standoff (through riseAvailable), so it is null when the standoff is
   * unknown. marginBottom defaults to DEFAULT_MARGIN_BOTTOM, which is 0; pass it to
   * override with a cushion. Null if the screen height, plate offset, or standoff is
   * unusable.
   *
   * [WARN] This REPLACES the old two-margin cap (screenHeight - plateOffset - 3 - 3
   * = 24.650 on the 70 inch example). On the 70 inch example at 9 in 12 it now reads
   * 27.901 at the default marginBottom of 0. See the header [WARN].
   */
  function pipeMaxSliding(opts) {
    opts = opts || {};
    var sh = num(opts.screenHeight);
    var po = num(opts.plateOffset);
    var ra = riseAvailable(opts.standoff, opts.riseOver12);
    var mb = num(opts.marginBottom);
    if (mb === null) mb = DEFAULT_MARGIN_BOTTOM;
    if (sh === null || po === null || ra === null) return null;
    return sh - po - mb - ra;
  }

  /**
   * [CAP-RESTORED 2026-08-29] VESTIGIAL. There IS a minimum screen height for the sliding head again (a
   * screen too short for the restored collar cap gives an empty window and 'no_pipe_fits'), but solve()
   * enforces it inline via the collar cap, NOT via this helper: this figure uses the OLD collar-UNAWARE,
   * marginBottom-aware cap, so it is about 0.65 in LOW versus the true collar-cap minimum and nothing in
   * the page calls it. solve() returns 'no_pipe_fits' (never 'screen_too_short') for a short sliding
   * screen. Kept, exported and tested for documentation only; do NOT reintroduce it as a gate.
   *
   * THE MINIMUM SCREEN HEIGHT the sliding head needs on a given plate, head, hole
   * position and pitch, in inches. A build works when the sliding floor fits under the
   * sliding cap:
   *   reach = holeMid - riseAvailable + HOLE_TO_PIPE_END - plateOffset   ([TRAVEL] reach floor)
   *   grip  = minPipeForSlidingHead()                                    (9.361, constant)
   *   floor = max(reach, grip)
   *   cap   = screenHeight - plateOffset - riseAvailable - marginBottom
   * floor <= cap rearranges to the least screen height that lets the sliding head be used:
   *   minScreen = floor + plateOffset + riseAvailable + marginBottom
   * This is the HONEST bound solve() enforces, and it agrees with solve() exactly: below it
   * solve() returns 'screen_too_short', at or above it the cap clears the floor and the head
   * can be used, so notice_sliding_no_fit can quote it without contradicting solve(). When the
   * reach floor binds (the usual build) the riseAvailable and plateOffset terms cancel and this
   * is simply holeMid + HOLE_TO_PIPE_END + marginBottom; when the grip floor binds (a shallow
   * build, small holeMid on a steep pitch) it is grip + plateOffset + riseAvailable + marginBottom.
   * On the owner's case (holeMid 18, plate 4.75, standoff 3.665, 12 in 12, marginBottom 0) that is
   * 25.036 in (18 + 7.036), so a 25 inch screen is too short and his 28 inch clears.
   *
   * [TRAVEL] length_offset (headOffset) is NO LONGER read here: the sliding head's reach is the
   * physical HOLE_TO_PIPE_END, not the -6.0 length_offset seed. See the [TRAVEL] block in solve().
   *
   * Accepts holeMid directly, or topToTopHole + vesaHeight to derive it, exactly as solve() does.
   *
   * NULL when holeMid or the plate offset is missing, or the standoff is unknown (riseAvailable
   * null), so the caller states no number rather than guessing. marginBottom defaults to
   * DEFAULT_MARGIN_BOTTOM (0, the shipped default); a cushion, if ever re-added, only raises it.
   */
  function minScreenHeightForSlidingHead(opts) {
    opts = opts || {};
    // [RULE] DERIVE holeMid THE SAME WAY solve() DOES, or a caller that hands this the
    // very opts object it hands solve() (topToTopHole + vesaHeight, no holeMid) gets a
    // spurious null. The doc comment above has always promised "accepts holeMid directly,
    // or topToTopHole + vesaHeight to derive it"; before this line only solve() honoured
    // it and this helper read holeMid alone, so the two disagreed on the same input.
    var holeMid = num(opts.holeMid);
    if (holeMid === null) holeMid = holeMidFromTop(opts.topToTopHole, opts.vesaHeight);
    var po = num(opts.plateOffset);
    var ra = riseAvailable(opts.standoff, opts.riseOver12);
    var mb = num(opts.marginBottom);
    if (mb === null) mb = DEFAULT_MARGIN_BOTTOM;
    if (holeMid === null || po === null || ra === null) return null;
    var reachFloor = holeMid - ra + HOLE_TO_PIPE_END - po;   // [TRAVEL] cornerFloor(no tilt) + HTPE - plate
    var floor = Math.max(reachFloor, minPipeForSlidingHead());
    return floor + po + ra + mb;
  }

  /**
   * Which stock pipes land inside a window, ascending, with the longest and
   * shortest called out. A null bound (an unknown min from an unknown standoff,
   * say) means that side is unconstrained. An empty or missing stock list, and a
   * window nothing fits, both come back with fits:[] and longest/shortest null,
   * never undefined and never a guess.
   *
   * @returns {{fits:number[], longest:(number|null), shortest:(number|null)}}
   */
  function fitStock(win, stockLengths) {
    var out = { fits: [], longest: null, shortest: null };
    if (!win || !stockLengths || !stockLengths.length) return out;
    var lo = num(win.min);   // null -> no lower bound
    var hi = num(win.max);   // null -> no upper bound
    var fits = [];
    for (var i = 0; i < stockLengths.length; i++) {
      var p = num(stockLengths[i]);
      if (p === null) continue;
      if (lo !== null && p < lo - EPS) continue;
      if (hi !== null && p > hi + EPS) continue;
      fits.push(p);
    }
    fits.sort(function (a, b) { return a - b; });
    out.fits = fits;
    if (fits.length) {
      out.shortest = fits[0];
      out.longest = fits[fits.length - 1];
    }
    return out;
  }

  /**
   * THE ONE CALL THE UI MAKES for a single plate + head + TV + pitch combination.
   * It routes to the sliding rule or the fixed rule on the head offset.
   *
   * FIXED head: the corner-clearance minimum is the only hard rule. Recommend the
   * SHORTEST stock pipe at or above minPipe (screen top as close to the ceiling as
   * the stock allows), offer the next four longer stock pipes as `options` (each with
   * its own plateGap), and report `plateGap` for the recommendation as a plain fact.
   * reason is 'ok' whenever any stock pipe clears the corner. `window` is attached for
   * a caller that wants the optional hidden-plate preference, but never gates here.
   *
   * SLIDING head: the head position sets the screen height, not the pipe, but the pipe
   * still has a range [floor, cap] (both HARD rules; [CAP-RESTORED 2026-08-29]). The floor
   * is max(reach, grip): the pipe must REACH from the plate down to the head (the binding one
   * on a normal build) AND the head and collar must GRIP it (minPipeForSlidingHead, the
   * absolute least, binding only on very shallow builds). The cap is the pipe that keeps the
   * THREADED COLLAR BOTTOM at or above the bottom edge of the screen (see the header). Recommend
   * the SHORTEST stock pipe in [floor, cap], offer the longer IN-WINDOW stock pipes as `options`,
   * set floorReason ('reach' or 'grip'), report `floor` and `cap`, and return the head-drop band
   * {min, max} (min = the highest-screen install drop; max = dMax, the head at the pipe end). If no
   * stock pipe lies in [floor, cap] the build is 'no_pipe_fits' (an empty window on a too-short
   * screen, or a gap between stock sizes), no recommendation. plateGap stays an OUTPUT (the plate-
   * above-the-screen rule is not a gate). See the header.
   *
   * Accepts holeMid directly, or topToTopHole + vesaHeight to derive it.
   */
  function solve(opts) {
    opts = opts || {};
    // Accept both key names. `stockLengths` is preferred; `stock` is an alias so a
    // caller (the section code is written by someone else) does not silently get an
    // empty result from the wrong key. See the exchange of 2026-08-23.
    var stock = opts.stockLengths || opts.stock || [];
    var slides = headSlides(opts.headOffset);

    // [CAP-RESTORED 2026-08-29] marginBottom is INERT. The sliding CAP is back as a HARD gate, but it is the
    // THREADED COLLAR cap (owner-CAD collar overhang), computed inline in the sliding branch and NOT from
    // marginBottom. The parameter is still ACCEPTED and defaulted here for call-site compatibility, but
    // solve() does not read it into any output. Do not add a new consumer; do not fold marginBottom back
    // into the cap or add any other margin. (The standalone pipeMaxSliding and minScreenHeightForSlidingHead
    // helpers still take it, but they are vestigial and neither is called by solve() - see their docs.)
    var mb = num(opts.marginBottom);
    if (mb === null) mb = DEFAULT_MARGIN_BOTTOM;

    var holeMid = num(opts.holeMid);
    if (holeMid === null) holeMid = holeMidFromTop(opts.topToTopHole, opts.vesaHeight);

    if (slides) {
      var po = num(opts.plateOffset);
      var sh = num(opts.screenHeight);
      // [TILT] The top back corner, rotated down by the screen tilt (identity at tiltDeg 0).
      // cornerFloor = cornerY - riseAvailable(cornerX) is the drop d'min at which the tilted
      // corner just clears; at zero tilt it is holeMid - riseAvailable(standoff), unchanged.
      var corner = tiltPoint({ x: opts.standoff, y: holeMid, standoff: opts.standoff,
                               tiltDeg: opts.tiltDeg, tiltPivot: opts.tiltPivot,
                               tiltAxisDrop: opts.tiltAxisDrop, tiltTable: opts.tiltTable });
      var raCorner = riseAvailable(corner.x, opts.riseOver12);
      var cornerFloor = (corner.y === null || raCorner === null) ? null : corner.y - raCorner;
      var standoffKnown = num(opts.standoff) !== null;
      // [RESTING 2026-08-29] length_offset (opts.headOffset) is NOT read for the sliding head's geometry.
      // headSlides() still routes on it upstream and the fixed branch still uses it; the sliding branch
      // works from the physical resting figure HOLE_TO_PIPE_END_RESTING (6.161) alone.

      // [TRAVEL] dMin is the TOP of the head's physical travel window (the socket mouth PIPE_ENGAGEMENT
      // below the pipe top): dMin = plateOffset + PIPE_ENGAGEMENT + HOLE_TO_SOCKET. It is the HIGHEST the
      // head can sit (11FC in, all the way up), and it depends only on the plate offset. Kept for the 11FC
      // headroom arithmetic and the drawing; it no longer gates feasibility.
      var dMinTravel = (po === null) ? null : po + PIPE_ENGAGEMENT + HOLE_TO_SOCKET;

      // [RESTING 2026-08-29] dRest(L) is the PLACED head drop for a resting build (no 11FC): the head body
      // bottom on the threaded collar, so the hole centre sits HOLE_TO_PIPE_END_RESTING (6.161) above the
      // pipe end. This is the LOWEST screen position on pipe L, and it is the default drawn position.
      function dRestOf(L) {
        var l = num(L);
        return (po === null || l === null) ? null : po + l - HOLE_TO_PIPE_END_RESTING;
      }

      // [RESTING 2026-08-29] THE RESTING REACH FLOOR: the shortest pipe whose RESTING drop clears the corner,
      // dRest(L) >= cornerFloor  <=>  L >= cornerFloor + HOLE_TO_PIPE_END_RESTING - plateOffset. This REPLACES
      // the old [CAP-RESTORED] reach floor (which used HOLE_TO_PIPE_END 7.036, the 11FC-in figure): resting
      // drops the head the full 11FC lower, so it clears the corner with a pipe COLLAR_11FC (0.875) shorter.
      var reachFloor = (cornerFloor === null || po === null)
        ? null
        : cornerFloor + HOLE_TO_PIPE_END_RESTING - po;
      // [RULE] A MISSING REACH FLOOR (WITH THE STANDOFF KNOWN) IS 'geometry_unknown'. reachFloor is null when
      // the TV geometry or the plate offset is not known yet (holeMid or plateOffset absent). An unknown
      // STANDOFF has its own honest answer ('standoff_unknown', below) and must not be reported here, so this
      // guard checks the standoff is known first (the same discipline as the previous model). dMin still
      // reports when the plate offset is known.
      if (reachFloor === null && standoffKnown) {
        return {
          slides: true,
          window: null,
          slidingCap: null,
          floor: null,
          cap: null,
          minPipe: null,
          plateGap: null,
          pipeBelowScreen: null,
          rise11fc: null,
          needs11fc: false,
          options: [],
          fits: [],
          recommended: null,
          headDrop: null,
          dMin: dMinTravel,
          dMax: null,
          travel: null,
          floorReason: null,
          feasible: null,
          reason: 'geometry_unknown'
        };
      }
      // [RESTING 2026-08-29] floor = max(resting reach, resting grip). The grip floor is now
      // minPipeForSlidingHeadResting() = 8.486 (only the head body runs through when resting; the 11FC is
      // out), a FLIP from the old 9.361 11FC-in grip so a 9 in pipe can now hold the head. floorReason names
      // which binds: 'reach' (the pipe must reach the corner) or 'grip' (the head body span, shallow builds).
      var gripFloor = minPipeForSlidingHeadResting();
      var floor = null, floorReason = null;
      if (reachFloor !== null) {
        if (reachFloor >= gripFloor - EPS) { floor = reachFloor; floorReason = 'reach'; }
        else { floor = gripFloor; floorReason = 'grip'; }
      }
      // [RESTING 2026-08-29] pipeBelowScreen(L): how far the THREADED COLLAR BOTTOM hangs below the BOTTOM
      // edge of the screen AT REST, signed inches. The collar bottom is (plateOffset + L + 0.65) below P; the
      // screen bottom is dRest(L) + (screenHeight - holeMid) below P. Substituting dRest(L) makes L cancel:
      // pipeBelowScreen = HOLE_TO_COLLAR_BOTTOM_RESTING - (screenHeight - holeMid) = 6.811 - (screen - holeMid),
      // the SAME for every pipe length. Positive = the collar shows below the screen (a 'collar_shows' build);
      // <= 0 = hidden. It is an OUTPUT, never a gate on pipe length (there is no cap any more).
      function pipeBelowScreen(L) {
        var l = num(L), dr = dRestOf(l);
        return (po === null || l === null || dr === null || sh === null || holeMid === null)
          ? null
          : (po + l + THREADED_COLLAR_BELOW_PIPE_IN) - (dr + (sh - holeMid));
      }
      // [RESTING 2026-08-29] plateGap at rest for pipe L: holeMid - dRest(L). Positive = the plate sits that
      // far BELOW the screen top (hidden/covered), negative = it stands PROUD (visible). An OUTPUT for the
      // mount-cover sentence, never a gate. A longer pipe rests lower (larger dRest), so its plateGap shrinks.
      function plateGapAtRest(L) {
        var dr = dRestOf(L);
        return (holeMid === null || dr === null) ? null : holeMid - dr;
      }
      // [RESTING 2026-08-29] rise11fc(L): the REALIZABLE upward rise if the OPTIONAL 11FC is added, in inches.
      // The 11FC is COLLAR_11FC (0.875 in) tall, so with it in the stack the head can sit no LOWER than
      // dRest(L) - COLLAR_11FC (there is a 0.875 in dead band between resting and the highest 11FC-in
      // position). The head can rise from rest (dRest) up to max(cornerFloor, dMin) - the corner/travel limit
      // - but only if that limit is at or below dRest - COLLAR_11FC. So the realizable rise is
      //   rise = (dRest(L) - COLLAR_11FC) - max(cornerFloor, dMin).
      // It is offered ONLY when rise > 0 (equivalently the RAW gap dRest - max(cornerFloor, dMin) >= 0.875).
      // The RAW gap is dRest(L) - max(cornerFloor, dMin); it is NOT the realizable figure because it ignores
      // the 11FC's own 0.875 in height, so a raw gap below 0.875 (e.g. the owner case 0.451) yields a
      // NEGATIVE rise and the 11FC is not offered. rise can be negative; the page gates on > 0.
      function rise11fcFor(L) {
        var dr = dRestOf(L);
        return (dr === null || cornerFloor === null || dMinTravel === null)
          ? null
          : (dr - COLLAR_11FC) - Math.max(cornerFloor, dMinTravel);
      }
      // [RESTING 2026-08-29] THE COLLAR-SHOWS GATE: a TV-geometry limit, tilt-independent and pipe-length
      // independent. When screenHeight - holeMid < HOLE_TO_COLLAR_BOTTOM_RESTING (6.811) the threaded collar
      // hangs below the screen bottom at rest on EVERY pipe, and no pipe can fix it. null when sh or holeMid
      // is not yet known (do not fail on a blank field).
      var collarClear = (sh === null || holeMid === null)
        ? null
        : (sh - holeMid) >= HOLE_TO_COLLAR_BOTTOM_RESTING - EPS;

      var sReason, sFeasible, sRecommended = null, sHeadDrop = null, sOptions = [], sPipeBelow = null,
          sPlateGap = null, sDMax = null, sTravel = null, sRise11fc = null, sFits = [];
      if (!standoffKnown) {
        // Nothing can be promised without the standoff: the corner floor needs it.
        sReason = 'standoff_unknown';
        sFeasible = null;
      } else if (collarClear === false) {
        // [RESTING 2026-08-29] The TV is too short: the threaded collar would show below the screen bottom at
        // rest, on every pipe (the resting bottom rule is L-independent). No pipe fixes it; the shopper needs
        // a taller screen or a different combination. Its own reason so the page can say exactly that.
        sReason = 'collar_shows';
        sFeasible = false;
      } else {
        // Every stock pipe at or above the resting floor is feasible: a MINIMUM pipe with no maximum, exactly
        // like the fixed head. Recommend the shortest; offer EVERY longer one (no cap).
        var asc = [];
        for (var i = 0; i < stock.length; i++) {
          var p = num(stock[i]);
          if (p !== null && floor !== null && p >= floor - EPS) asc.push(p);
        }
        asc.sort(function (a, b) { return a - b; });
        sFits = asc;
        if (!asc.length) {
          // Pathological: no stock pipe is long enough to reach the corner at rest (a very deep corner floor
          // beyond the longest stock, or a degenerate pool). Kept for shape parity with the fixed head.
          sReason = 'no_pipe_fits';
          sFeasible = true;
        } else {
          sReason = 'ok';
          sFeasible = true;
          sRecommended = asc[0];                      // shortest stock pipe at or above the resting floor
          var drRec = dRestOf(sRecommended);
          sPlateGap = plateGapAtRest(sRecommended);   // resting plate gap (default position)
          sPipeBelow = pipeBelowScreen(sRecommended); // collar bottom vs screen bottom (<= 0 here)
          sRise11fc = rise11fcFor(sRecommended);      // 11FC realizable rise (offered only when > 0)
          // [RESTING 2026-08-29] The head-drop band for the RECOMMENDED pipe. min is the HIGHEST the head can
          // sit (11FC all the way up), max(cornerFloor, dMin); max is the RESTING drop dRest(L), the default
          // drawn position (no 11FC). The page draws at headDrop.max by default and at headDrop.min when the
          // optional 11FC is turned on. The RAW gap is headDrop.max - headDrop.min; the realizable
          // rise (rise11fc) subtracts the 11FC's own 0.875 in height.
          sHeadDrop = { min: Math.max(cornerFloor, dMinTravel), max: drRec };
          sDMax = drRec;                              // resting drop is the default placed position
          sTravel = (dMinTravel === null) ? null : drRec - dMinTravel;
          sOptions = asc.slice(1).map(function (pp) {
            return { pipe: pp, plateGap: plateGapAtRest(pp), pipeBelowScreen: pipeBelowScreen(pp),
                     rise11fc: rise11fcFor(pp) };
          });
        }
      }

      return {
        slides: true,
        window: null,
        // [RESTING 2026-08-29] The cap concept is gone (the head rests on the collar; there is no upper bound
        // on pipe length). slidingCap and cap are null for shape parity. floor is the resting minimum pipe.
        slidingCap: null,
        floor: (standoffKnown ? floor : null),
        cap: null,
        minPipe: null,
        // [RESTING 2026-08-29] plateGap (resting, holeMid - dRest) and pipeBelowScreen (collar bottom vs the
        // screen bottom, <= 0 when feasible) are OUTPUTS for the recommended pipe; each option carries its own.
        plateGap: sPlateGap,
        pipeBelowScreen: sPipeBelow,
        // [RESTING 2026-08-29] rise11fc is the REALIZABLE upward rise the OPTIONAL 11FC would give, in inches
        // (= (dRest - COLLAR_11FC) - max(cornerFloor, dMin); may be negative). needs11fc is FALSE for every
        // single-pipe build (the 11FC is never required, only offered when rise11fc > 0). The RAW gap is
        // headDrop.max - headDrop.min; rise11fc subtracts the 11FC's 0.875 in height, so a raw gap below 0.875
        // (owner case 0.451) gives a negative rise and the page does not offer the 11FC.
        rise11fc: sRise11fc,
        needs11fc: false,
        options: sOptions,
        fits: sFits,
        recommended: sRecommended,
        headDrop: sHeadDrop,
        // [TRAVEL] dMin is the top of the travel window (11FC all the way up; null when plateOffset is
        // missing). dMax is the RESTING drop for the recommended pipe (the default position); travel is
        // dMax - dMin. All kept for the 11FC headroom arithmetic and the drawing, not for feasibility.
        dMin: dMinTravel,
        dMax: sDMax,
        travel: sTravel,
        // Which floor set the recommendation: 'reach' (the resting pipe must reach the corner) or 'grip'
        // (the head body span, shallow builds). Only meaningful when a pipe was recommended.
        floorReason: floorReason,
        feasible: sFeasible,
        reason: sReason
      };
    }

    // FIXED head. The corner-clearance minimum is the only hard rule.
    var win = pipeWindow({
      holeMid: holeMid,
      headOffset: opts.headOffset,
      plateOffset: opts.plateOffset,
      standoff: opts.standoff,
      riseOver12: opts.riseOver12
    });
    var zeroPipe = win.zeroMarginPipe;
    var minPipe = minPipeForCorner({
      holeMid: holeMid,
      headOffset: opts.headOffset,
      plateOffset: opts.plateOffset,
      standoff: opts.standoff,
      riseOver12: opts.riseOver12,
      tiltDeg: opts.tiltDeg,          // [TILT] shorter minimum pipe as the screen tilts down
      tiltPivot: opts.tiltPivot,
      tiltAxisDrop: opts.tiltAxisDrop,
      tiltTable: opts.tiltTable
    });

    var fReason, fFeasible, fRecommended = null, fPlateGap = null, fOptions = [], fFits = [];
    if (num(opts.standoff) === null) {
      // The corner minimum needs the standoff. Suppress the pick until it is known.
      fReason = 'standoff_unknown';
      fFeasible = null;
    } else if (zeroPipe === null || minPipe === null) {
      // Unusable geometry (a missing measurement). No pick.
      fReason = 'screen_too_short';
      fFeasible = false;
    } else {
      var asc = [];
      for (var i = 0; i < stock.length; i++) {
        var p = num(stock[i]);
        if (p !== null && p >= minPipe - EPS) asc.push(p);
      }
      asc.sort(function (a, b) { return a - b; });
      fFits = asc;
      if (!asc.length) {
        // No stock pipe is long enough to clear the corner. With the real stock this
        // does not happen (see the header); it is kept for a pathological short pool.
        fReason = 'no_pipe_fits';
        fFeasible = true;
      } else {
        fRecommended = asc[0];                 // shortest at or above the minimum
        fPlateGap = zeroPipe - fRecommended;   // signed: + hidden, - proud, OUTPUT only
        fOptions = asc.slice(1, 5).map(function (pp) {
          return { pipe: pp, plateGap: zeroPipe - pp };
        });
        fReason = 'ok';
        fFeasible = true;
      }
    }

    return {
      slides: false,
      window: win,
      slidingCap: null,
      // A fixed head threads onto the pipe END, so it has no [floor, cap] window; null for shape parity.
      floor: null,
      cap: null,
      minPipe: minPipe,
      plateGap: fPlateGap,
      // A fixed head threads onto the pipe END, so nothing protrudes below the TV: pipeBelowScreen
      // is a sliding-head concept only, null here for shape parity with the sliding result.
      pipeBelowScreen: null,
      // [RESTING 2026-08-29] rise11fc / needs11fc are sliding-head concepts (the optional 11FC upgrade);
      // null / false here for shape parity with the sliding result.
      rise11fc: null,
      needs11fc: false,
      options: fOptions,
      fits: fFits,
      recommended: fRecommended,
      headDrop: null,
      // A fixed head has no sliding floor at all (it threads onto the pipe end), so there is
      // no floor to name. Kept for shape parity with the sliding result.
      floorReason: null,
      feasible: fFeasible,
      reason: fReason
    };
  }

  window.VMSSlope = {
    DEFAULT_MARGIN_BOTTOM: DEFAULT_MARGIN_BOTTOM,
    SLIDING_HEAD_OFFSET_MAX: SLIDING_HEAD_OFFSET_MAX,
    WALL_PITCH_MIN: WALL_PITCH_MIN,
    MEASURED_STANDOFF: MEASURED_STANDOFF,
    HEAD_BODY: HEAD_BODY,
    COLLAR_11FC: COLLAR_11FC,
    SLIDING_HEAD_SPAN: SLIDING_HEAD_SPAN,
    PIPE_ENGAGEMENT: PIPE_ENGAGEMENT,
    HOLE_TO_SOCKET: HOLE_TO_SOCKET,
    HOLE_TO_PIPE_END: HOLE_TO_PIPE_END,
    HOLE_TO_PIPE_END_RESTING: HOLE_TO_PIPE_END_RESTING,
    THREADED_COLLAR_BELOW_PIPE_IN: THREADED_COLLAR_BELOW_PIPE_IN,
    HOLE_TO_COLLAR_BOTTOM_RESTING: HOLE_TO_COLLAR_BOTTOM_RESTING,
    MIGRATING_VESA_PATH: MIGRATING_VESA_PATH,
    num: num,
    pitchDegrees: pitchDegrees,
    pitchFromDegrees: pitchFromDegrees,
    holeMidFromTop: holeMidFromTop,
    riseAvailable: riseAvailable,
    headSlides: headSlides,
    readsAsWall: readsAsWall,
    tiltPoint: tiltPoint,
    minPipeForCorner: minPipeForCorner,
    minPipeForSlidingHead: minPipeForSlidingHead,
    minPipeForSlidingHeadResting: minPipeForSlidingHeadResting,
    plateGap: plateGap,
    pipeWindow: pipeWindow,
    pipeMaxSliding: pipeMaxSliding,
    minScreenHeightForSlidingHead: minScreenHeightForSlidingHead,
    fitStock: fitStock,
    solve: solve
  };
})();
